import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { GeneratorConfigV1 } from '../types/generator-config';
import { isGeneratorConfigV1 } from '../types/generator-config';

export interface PublicOrg {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    brand_color: string | null;
}

export interface PublicGenerator {
    id: string;
    organization_id: string;
    name: string;
    slug: string;
    industry: string | null;
    config: GeneratorConfigV1 | null;
    is_published: boolean;
    org: PublicOrg;
}

interface UsePublicGeneratorResult {
    loading: boolean;
    error: string | null;
    generator: PublicGenerator | null;
}

/**
 * Charge un générateur publié par (org_slug, gen_slug), sans auth nécessaire.
 * Nécessite que les policies RLS publiques soient appliquées (public_access.sql).
 */
export function usePublicGenerator(
    orgSlug: string | undefined,
    genSlug: string | undefined
): UsePublicGeneratorResult {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [generator, setGenerator] = useState<PublicGenerator | null>(null);

    useEffect(() => {
        if (!orgSlug || !genSlug) {
            setLoading(false);
            setError('Lien incomplet.');
            return;
        }

        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setError(null);

            const { data: org, error: orgErr } = await supabase
                .from('organizations')
                .select('id, name, slug, logo_url, brand_color')
                .eq('slug', orgSlug)
                .maybeSingle();

            if (cancelled) return;
            if (orgErr) { setError(orgErr.message); setLoading(false); return; }
            if (!org) { setError('Organisation introuvable.'); setLoading(false); return; }

            const { data: gen, error: genErr } = await supabase
                .from('generators')
                .select('*')
                .eq('organization_id', org.id)
                .eq('slug', genSlug)
                .eq('is_published', true)
                .maybeSingle();

            if (cancelled) return;
            if (genErr) { setError(genErr.message); setLoading(false); return; }
            if (!gen) {
                setError("Ce générateur n'existe pas ou n'est pas publié.");
                setLoading(false);
                return;
            }

            const config = isGeneratorConfigV1(gen.config) ? gen.config : null;

            setGenerator({
                id: gen.id,
                organization_id: gen.organization_id,
                name: gen.name,
                slug: gen.slug,
                industry: gen.industry,
                is_published: gen.is_published,
                config,
                org: org as PublicOrg,
            });
            setLoading(false);
        };

        void run();
        return () => { cancelled = true; };
    }, [orgSlug, genSlug]);

    return { loading, error, generator };
}
